/**
 * DateRangeControl — preset picker + custom [from, to] inputs
 * (IA §1.2 date-range control, Plan 02-04 Task 4.3).
 *
 * Six options: Last 24h / Last 7d / Last 30d / Last 90d / All time / Custom.
 * When "Custom" is selected, two native <input type="date"> fields appear
 * alongside; changes propagate up via `onChange({ preset, from?, to? })`.
 *
 * Controlled component — the parent (FilterBar) owns state in
 * `useUrlFilters().pendingFilters` and renders this bound to that slice.
 */

import { useRef } from 'react';
import { Calendar } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Filters } from '@/lib/filters-schema';

type Preset = NonNullable<Filters['preset']>;

const PRESET_OPTIONS: { value: Preset; label: string }[] = [
  { value: '24h', label: 'Last 24h' },
  { value: '7d', label: 'Last 7d' },
  { value: '30d', label: 'Last 30d' },
  { value: '90d', label: 'Last 90d' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

export interface DateRangeControlValue {
  preset?: Preset;
  from?: string;
  to?: string;
}

export interface DateRangeControlProps {
  value: DateRangeControlValue;
  onChange: (next: DateRangeControlValue) => void;
  /**
   * Stacked mobile variant used inside FilterModal — hides the inline "Date:"
   * label (the modal renders its own block-level label above the control),
   * stretches the preset Select trigger + custom-range inputs to full width,
   * and bumps touch targets to 44px. Desktop FilterBar leaves this undefined
   * and keeps the original inline row layout.
   */
  stacked?: boolean;
  inapplicableReason?: string;
}

export function DateRangeControl({
  value,
  onChange,
  stacked = false,
  inapplicableReason,
}: DateRangeControlProps) {
  // Default the visible preset to '30d' (matches DEFAULT_FILTERS) when the URL
  // hasn't set one — keeps the Select from showing an empty trigger.
  const currentPreset: Preset = value.preset ?? '30d';

  // P0 — defer onChange until Radix Select finishes its close animation.
  //
  // Repro: opening the Date preset and switching 2-3 times (7d→24h→30d→all)
  // left `<html>` stuck with `pointer-events: none`. Radix Select applies
  // that style during open and removes it in a cleanup effect on unmount;
  // calling onChange() from inside onValueChange triggers a synchronous
  // setPending → router navigate → re-render that races with Radix'
  // cleanup, and cleanup sometimes loses, leaving the whole viewport
  // inert until a reload. Deferring the state update to the next tick
  // (after the Select actually closes) keeps Radix' cleanup on the happy
  // path. See: https://github.com/radix-ui/primitives/issues/1241.
  const pendingRef = useRef<DateRangeControlValue | null>(null);

  const queueChange = (next: DateRangeControlValue) => {
    pendingRef.current = next;
  };

  const flushPending = () => {
    const next = pendingRef.current;
    if (!next) return;
    pendingRef.current = null;
    // rAF lands us strictly after Radix' close effect runs; setTimeout(fn, 0)
    // works too but rAF is the commonly-cited cure for this bug.
    requestAnimationFrame(() => onChange(next));
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) flushPending();
  };

  const handlePresetChange = (next: Preset) => {
    if (next === 'custom') {
      queueChange({ preset: 'custom', from: value.from, to: value.to });
    } else {
      // Non-custom presets own their own [from,to]; clear any explicit window
      // so resolveRange() uses presetToRange() on the next apply.
      queueChange({ preset: next, from: undefined, to: undefined });
    }
  };

  if (stacked) {
    // Mobile modal variant: block-level, full-width, 44px touch targets.
    // The modal's own <Label>Date</Label> sits above us — we don't render
    // our own inline label to avoid "Date: Date" duplication.
    return (
      <div className="flex flex-col gap-2">
        {inapplicableReason && <p id="filter-date-inapplicable" data-testid="filter-date-inapplicable" className="text-xs text-muted-foreground">{inapplicableReason}</p>}
        <Label
          htmlFor="filter-date-preset"
          className="text-sm font-medium"
        >
          Date
        </Label>
        <Select
          disabled={Boolean(inapplicableReason)}
          value={currentPreset}
          onValueChange={handlePresetChange}
          onOpenChange={handleOpenChange}
        >
          <SelectTrigger
            disabled={Boolean(inapplicableReason)}
            title={inapplicableReason}
            aria-describedby={inapplicableReason ? "filter-date-inapplicable" : undefined}
            id="filter-date-preset"
            // focus-visible (keyboard) shows the 2px ring; plain focus
            // (mouse blur aftermath) is suppressed so the trigger doesn't
            // keep a glow after the dropdown closes.
            className="min-h-11 w-full focus:ring-0 focus-visible:ring-2"
          >
            <SelectValue />
          </SelectTrigger>
          {/*
            P1 — raise SelectContent above the mobile FilterModal Dialog
            (overlay/content sit at z-50) so the popover isn't clipped or
            tap-swallowed. `bg-theme-surface` keeps the panel opaque so
            Cohort Select text below can't bleed through.
          */}
          <SelectContent className="z-[60] bg-theme-surface">
            {PRESET_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {currentPreset === 'custom' && (
          <div className="flex items-center gap-2">
            <div className="flex flex-1 flex-col gap-1">
              <Label
                htmlFor="filter-date-from"
                className="text-xs text-muted-foreground"
              >
                From
              </Label>
              <Input
                disabled={Boolean(inapplicableReason)}
                title={inapplicableReason}
                id="filter-date-from"
                type="date"
                className="min-h-11 w-full"
                value={value.from ?? ''}
                onChange={(e) =>
                  onChange({
                    preset: 'custom',
                    from: e.target.value,
                    to: value.to,
                  })
                }
              />
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <Label
                htmlFor="filter-date-to"
                className="text-xs text-muted-foreground"
              >
                To
              </Label>
              <Input
                disabled={Boolean(inapplicableReason)}
                title={inapplicableReason}
                id="filter-date-to"
                type="date"
                className="min-h-11 w-full"
                value={value.to ?? ''}
                onChange={(e) =>
                  onChange({
                    preset: 'custom',
                    from: value.from,
                    to: e.target.value,
                  })
                }
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5" title={inapplicableReason}>
      {inapplicableReason && <span id="filter-date-inapplicable" data-testid="filter-date-inapplicable" className="sr-only">{inapplicableReason}</span>}
      <Label className="sr-only" htmlFor="filter-date-preset">
        Date:
      </Label>
      <div className="relative">
        {/*
          Calendar icon is positioned absolutely so it sits to the LEFT of
          the SelectValue text. We can't put it as a sibling of <SelectValue />
          inside <SelectTrigger> because shadcn's SelectTrigger applies
          `[&>span]:line-clamp-1` which forces `display: -webkit-box` on any
          direct span child — that would re-flow our Calendar icon onto a
          second line above the text, making the trigger appear two-row.
        */}
        <Calendar
          className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground"
          aria-hidden="true"
        />
        <Select
          disabled={Boolean(inapplicableReason)}
          value={currentPreset}
          onValueChange={handlePresetChange}
          onOpenChange={handleOpenChange}
        >
          <SelectTrigger
            disabled={Boolean(inapplicableReason)}
            title={inapplicableReason}
            aria-describedby={inapplicableReason ? "filter-date-inapplicable" : undefined}
            id="filter-date-preset"
            aria-label="Date range"
            className="h-8 w-[128px] pl-7 pr-2 focus:ring-0 focus-visible:ring-2"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-theme-surface">
            {PRESET_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {currentPreset === 'custom' && (
        <div className="flex items-center gap-1">
          <Label htmlFor="filter-date-from" className="sr-only">
            From
          </Label>
          <Input
                disabled={Boolean(inapplicableReason)}
                title={inapplicableReason}
            id="filter-date-from"
            type="date"
            className="h-9 w-[140px]"
            value={value.from ?? ''}
            onChange={(e) =>
              onChange({ preset: 'custom', from: e.target.value, to: value.to })
            }
          />
          <span className="text-muted-foreground">→</span>
          <Label htmlFor="filter-date-to" className="sr-only">
            To
          </Label>
          <Input
                disabled={Boolean(inapplicableReason)}
                title={inapplicableReason}
            id="filter-date-to"
            type="date"
            className="h-9 w-[140px]"
            value={value.to ?? ''}
            onChange={(e) =>
              onChange({ preset: 'custom', from: value.from, to: e.target.value })
            }
          />
        </div>
      )}
    </div>
  );
}
